import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "@/contexts/ToastContext";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { Aviso, Cargando, FilaHecha, INPUT_CLASS } from "./ui";
import type { PasoProps } from "./types";

/**
 * PASO 4 (citas): salas o cabinas. OPCIONAL DE VERDAD, y el texto lo dice.
 *
 * Comprobado en el código antes de escribir esta pantalla: el motor de huecos
 * (`public-appointments/availability.ts`) NO consulta las salas, y al crear una
 * cita se le asigna una sala libre si la hay y, si no la hay, la cita se crea
 * igual sin sala. Así que las salas sirven para SABER DÓNDE se atiende y para
 * ver la agenda ordenada, no para topar las citas que se pueden coger. Prometer
 * aquí que "sin salas no se puede dar cita" sería mentira, y prometer que
 * "limitan las citas a la vez" también.
 */

interface Sala {
  id: string;
  name: string;
}

export default function PasoSalas({
  estado,
  refrescar,
  registrarAvance,
  registrarTextoAvance,
}: PasoProps) {
  const { showToast } = useGlobalToast();
  const [salas, setSalas] = useState<Sala[] | null>(null);
  const [nombre, setNombre] = useState("");
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const res = await apiClient.get("/appointment-rooms");
      setSalas(res.data?.data ?? []);
    } catch {
      setSalas([]);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  useEffect(() => {
    // Este paso nunca bloquea: se puede seguir sin salas.
    registrarAvance(null);
    registrarTextoAvance((salas?.length ?? 0) > 0 ? null : "No uso salas, seguir");
    return () => registrarTextoAvance(null);
  }, [salas, registrarAvance, registrarTextoAvance]);

  const crear = async () => {
    const limpio = nombre.trim();
    if (!limpio) {
      showToast("Escribe el nombre de la sala", "error");
      return;
    }
    try {
      setCreando(true);
      await apiClient.post("/appointment-rooms", {
        name: limpio,
        isActive: true,
        ...(estado.sede ? { locationId: estado.sede.id } : {}),
      });
      setNombre("");
      await cargar();
      refrescar();
      showToast("Sala creada");
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo crear la sala"), "error");
    } finally {
      setCreando(false);
    }
  };

  if (!salas) return <Cargando texto="Cargando tus salas…" />;

  return (
    <div className="space-y-4">
      <Aviso>
        Si atiendes en <strong>cabinas, boxes o salas</strong> y quieres ver en
        qué sitio se hace cada cita, créalos aquí. Es opcional:{" "}
        <strong>tus clientes pueden pedir cita sin salas</strong>, y cada cita se
        coloca sola en una libre cuando existen.
      </Aviso>

      {salas.length > 0 && (
        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 px-3">
          {salas.map((s) => (
            <FilaHecha key={s.id} titulo={s.name} />
          ))}
        </div>
      )}

      <div className="rounded-lg border border-dashed border-gray-300 p-3 space-y-3">
        <p className="text-sm font-medium text-gray-900">
          {salas.length === 0 ? "Crear una sala" : "Añadir otra sala"}
        </p>
        <input
          className={INPUT_CLASS}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Cabina 1"
          maxLength={100}
          aria-label="Nombre de la sala"
        />
        <button
          type="button"
          onClick={crear}
          disabled={creando}
          className="inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          {creando ? "Creando…" : "Añadir sala"}
        </button>
      </div>
    </div>
  );
}
